import { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemText,
  Alert,
  Snackbar,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useCollection } from '../hooks/useFirestore';
import { query as fbQuery, orderBy as fbOrderBy } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import { functions } from '../firebase';

// 手数料率（概算表示用）。実際の手数料は教室ごとに設定可能なため概算値。
const FEE_PERCENT = 7.5;

interface Payment {
  id: string;
  schoolId: string;
  schoolNameSnapshot?: string;
  amount: number;
  currency?: string;
  method?: 'credit' | 'cash';
  status?: string;
  refundedAmount?: number;
  stripePaymentIntentId?: string | null;
  createdAt?: any;
  paidAt?: any;
}

const toDate = (v: any): Date | null => {
  if (!v) return null;
  try {
    return typeof v?.toDate === 'function' ? v.toDate() : new Date(v);
  } catch {
    return null;
  }
};

const refundState = (p: Payment) => {
  const refunded = Number(p.refundedAmount || 0);
  if (refunded <= 0) return 'none';
  if (refunded >= Number(p.amount || 0)) return 'full';
  return 'partial';
};

export default function StripePage() {
  // 実データは payments コレクション。Stripe(クレジット)決済かつ確定(paid)分を対象にする。
  const { data: allPayments, loading } = useCollection<Payment>(
    'payments',
    (q) => fbQuery(q, fbOrderBy('createdAt', 'desc'))
  );

  const payments = (allPayments || []).filter(
    (p) => p.method === 'credit' && p.status === 'paid'
  );

  const [showRefundDialog, setShowRefundDialog] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [refundAmount, setRefundAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  const openRefund = (p: Payment) => {
    setSelectedPayment(p);
    const remaining = Number(p.amount || 0) - Number(p.refundedAmount || 0);
    setRefundAmount(String(Math.max(0, remaining)));
    setError('');
    setShowRefundDialog(true);
  };

  const handleRefund = async () => {
    if (!selectedPayment) return;
    const amount = Number(refundAmount);
    const remaining =
      Number(selectedPayment.amount || 0) - Number(selectedPayment.refundedAmount || 0);
    if (!Number.isFinite(amount) || amount <= 0) {
      setError('返金額を正しく入力してください。');
      return;
    }
    if (amount > remaining) {
      setError(`返金可能額（¥${remaining.toLocaleString()}）を超えています。`);
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const processRefund = httpsCallable(functions, 'processRefund');
      await processRefund({ paymentId: selectedPayment.id, amount });
      setShowRefundDialog(false);
      setSelectedPayment(null);
      setRefundAmount('');
      setToast('返金処理が完了しました。');
    } catch (e: any) {
      console.error('Failed to process refund:', e);
      setError(e?.message || '返金処理に失敗しました。');
    } finally {
      setSubmitting(false);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'schoolNameSnapshot',
      headerName: '教室名',
      flex: 1,
      renderCell: (params) => params.value || params.row.schoolId || '-',
    },
    {
      field: 'amount',
      headerName: '決済金額',
      width: 130,
      renderCell: (params) => `¥${Number(params.value ?? 0).toLocaleString()}`,
    },
    {
      field: 'fee',
      headerName: `手数料(${FEE_PERCENT}%概算)`,
      width: 150,
      renderCell: (params) =>
        `¥${Math.round(Number(params.row.amount ?? 0) * (FEE_PERCENT / 100)).toLocaleString()}`,
    },
    {
      field: 'refundedAmount',
      headerName: '返金額',
      width: 120,
      renderCell: (params) => `¥${Number(params.value ?? 0).toLocaleString()}`,
    },
    {
      field: 'status',
      headerName: 'ステータス',
      width: 120,
      renderCell: (params) => {
        const s = refundState(params.row);
        return s === 'full' ? '返金済み' : s === 'partial' ? '一部返金' : '決済完了';
      },
    },
    {
      field: 'createdAt',
      headerName: '決済日時',
      width: 170,
      renderCell: (params) => {
        const d = toDate(params.row.paidAt) || toDate(params.value);
        return d ? format(d, 'yyyy/MM/dd HH:mm', { locale: ja }) : '-';
      },
    },
    {
      field: 'actions',
      headerName: '操作',
      width: 100,
      sortable: false,
      renderCell: (params) => {
        const remaining =
          Number(params.row.amount || 0) - Number(params.row.refundedAmount || 0);
        return remaining > 0 ? (
          <Button size="small" color="error" onClick={() => openRefund(params.row)}>
            返金
          </Button>
        ) : null;
      },
    },
  ];

  // 教室ごとの集計を計算
  const schoolSummaries = payments.reduce((acc, p) => {
    const key = p.schoolId || p.id;
    if (!acc[key]) {
      acc[key] = {
        schoolName: p.schoolNameSnapshot || p.schoolId || '-',
        totalAmount: 0,
        totalRefunded: 0,
        count: 0,
      };
    }
    acc[key].totalAmount += Number(p.amount || 0);
    acc[key].totalRefunded += Number(p.refundedAmount || 0);
    acc[key].count += 1;
    return acc;
  }, {} as Record<string, { schoolName: string; totalAmount: number; totalRefunded: number; count: number }>);

  const remaining = selectedPayment
    ? Number(selectedPayment.amount || 0) - Number(selectedPayment.refundedAmount || 0)
    : 0;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Stripe決済管理
      </Typography>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                教室別集計
              </Typography>
              <List dense>
                {Object.entries(schoolSummaries).map(([schoolId, s]) => (
                  <ListItem key={schoolId}>
                    <ListItemText
                      primary={s.schoolName}
                      secondary={`決済総額: ¥${s.totalAmount.toLocaleString()} / 返金: ¥${s.totalRefunded.toLocaleString()} / 件数: ${s.count}`}
                    />
                  </ListItem>
                ))}
                {Object.keys(schoolSummaries).length === 0 && (
                  <ListItem>
                    <ListItemText primary="決済データがありません" />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ height: 'calc(100vh - 400px)' }}>
        <DataGrid
          rows={payments}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
        />
      </Card>

      <Dialog
        open={showRefundDialog}
        onClose={() => !submitting && setShowRefundDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>返金処理</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            {selectedPayment?.schoolNameSnapshot || selectedPayment?.schoolId} の決済
            （決済額 ¥{Number(selectedPayment?.amount || 0).toLocaleString()} / 返金済み ¥
            {Number(selectedPayment?.refundedAmount || 0).toLocaleString()}）を返金します。
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            返金可能額: ¥{remaining.toLocaleString()}
          </Typography>
          <TextField
            fullWidth
            type="number"
            label="返金額"
            value={refundAmount}
            onChange={(e) => setRefundAmount(e.target.value)}
            sx={{ mt: 2 }}
          />
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRefundDialog(false)} disabled={submitting}>
            キャンセル
          </Button>
          <Button onClick={handleRefund} color="error" disabled={submitting}>
            {submitting ? '処理中...' : '返金する'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast('')}
        message={toast}
      />
    </Box>
  );
}
